<template>
  <div class="ai-chatbot">
    <!-- 聊天窗口 -->
    <transition name="slide-up">
      <div v-if="isOpen" class="chat-window">
        <!-- 头部 -->
        <div class="chat-header">
          <div class="header-left">
            <i class="el-icon-chat-dot-round"></i>
            <span>AI助手</span>
          </div>
          <div class="header-right">
            <el-tooltip content="清空聊天记录" placement="top">
              <i class="el-icon-delete" @click="clearChat"></i>
            </el-tooltip>
            <i class="el-icon-close" @click="toggleChat"></i>
          </div>
        </div>

        <!-- 消息列表 -->
        <div class="chat-messages" ref="messagesContainer">
          <div v-if="messages.length === 0" class="empty-state">
            <i class="el-icon-chat-line-round"></i>
            <p>你好! 我是AI助手</p>
            <p class="tips">有什么可以帮助你的吗?</p>
          </div>

          <div
              v-for="(msg, index) in messages"
              :key="index"
              :class="['message-item', msg.type]"
          >
            <div class="message-avatar">
              <i :class="msg.type === 'user' ? 'el-icon-user-solid' : 'el-icon-service'"></i>
            </div>
            <div class="message-content">
              <div class="message-text">{{ msg.content }}</div>
              <div class="message-time">{{ msg.time }}</div>
            </div>
          </div>

          <!-- 加载中动画 -->
          <div v-if="isLoading" class="message-item bot">
            <div class="message-avatar">
              <i class="el-icon-service"></i>
            </div>
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- 输入框 -->
        <div class="chat-input">
          <el-input
              v-model="inputText"
              placeholder="输入消息..."
              @keyup.enter.native="sendMessage"
              :disabled="isLoading"
          ></el-input>
          <el-button
              class="send-button"
              icon="el-icon-s-promotion"
              @click="sendMessage"
              :loading="isLoading"
              :disabled="!inputText.trim() && !isLoading"
              circle
          ></el-button>
        </div>
      </div>
    </transition>

    <!-- 浮动按钮 -->
    <transition name="bounce">
      <div
          v-if="!isOpen"
          class="chat-button"
          @click="toggleChat"
      >
        <i class="el-icon-chat-dot-round"></i>
        <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
      </div>
    </transition>
  </div>
</template>

<script>

export default {
  name: 'AIChatbot',
  data() {
    return {
      isOpen: false,
      inputText: '',
      messages: [],
      isLoading: false,
      unreadCount: 0,
      eventSource: null, // 保存 EventSource 实例
    }
  },
  mounted() {
    // 从本地存储加载聊天记录
    this.loadChatHistory()
  },
  beforeDestroy() {
    // 组件销毁前关闭 EventSource 连接
    if (this.eventSource) {
      this.eventSource.close()
    }
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.unreadCount = 0
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    async sendMessage() {
      if (!this.inputText.trim() || this.isLoading) return

      // 用户消息
      const userMessage = {
        type: 'user',
        content: this.inputText,
        time: this.formatTime(new Date()),
      }

      this.messages.push(userMessage)
      const userInput = this.inputText
      this.inputText = ''
      this.isLoading = true

      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        // 调用AI接口（流式）
        await this.callAIAPI(userInput)

        if (!this.isOpen) {
          this.unreadCount++
        }
      } catch (error) {
        console.error('AI调用失败:', error)
        const errorMessage = {
          type: 'bot',
          content: '抱歉,我遇到了一些问题,请稍后再试。',
          time: this.formatTime(new Date()),
        }
        this.messages.push(errorMessage)
        this.isLoading = false
      } finally {
        this.saveChatHistory()
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    async callAIAPI(message) {
      return new Promise((resolve, reject) => {
        try {
          //const apiHost = "http://localhost:8080"
            const apiHost = "https://shop.dakeaii.com"
          const token = localStorage.getItem("token")

          // 关闭之前的连接（如果有）
          if (this.eventSource) {
            this.eventSource.close()
          }

          // 创建 EventSource 连接
          const url = `${apiHost}/front/api/design/knowledge/stream?token=${token}&message=${encodeURIComponent(message)}`
          this.eventSource = new EventSource(url)

          // 创建机器人消息对象
          const botMessage = {
            type: 'bot',
            content: '',
            time: this.formatTime(new Date()),
          }

          // 先添加一个空的机器人消息
          this.messages.push(botMessage)
          const messageIndex = this.messages.length - 1

          // 监听消息事件
          this.eventSource.onmessage = (event) => {
            console.log('收到原始数据:', event.data)

            try {
              // 直接使用 event.data，无需额外解码
              let data = event.data

              // 如果数据为空或undefined，跳过
              if (!data) {
                return
              }

              // 将收到的数据追加到机器人消息内容中
              this.messages[messageIndex].content += data

              // 强制更新视图
              this.$forceUpdate()

              // 滚动到底部
              this.$nextTick(() => {
                this.scrollToBottom()
              })
            } catch (error) {
              console.error('处理数据时出错:', error)
            }
          }

          // 监听自定义 'end' 事件（如果后端发送了）
          this.eventSource.addEventListener('end', () => {
            console.log('流式传输结束')
            this.eventSource.close()
            this.eventSource = null
            this.isLoading = false
            resolve()
          })

          // 错误处理
          this.eventSource.onerror = (error) => {
            console.error('EventSource 错误:', error)
            this.eventSource.close()
            this.eventSource = null
            this.isLoading = false

            // 如果没有收到任何内容，显示错误消息
            if (this.messages[messageIndex].content === '') {
              this.messages[messageIndex].content = '连接失败，请检查网络或稍后重试。'
            }

            reject(error)
          }

          // 设置超时（可选，防止连接一直挂起）
          setTimeout(() => {
            if (this.isLoading && this.eventSource) {
              console.log('请求超时，关闭连接')
              this.eventSource.close()
              this.eventSource = null
              this.isLoading = false

              if (this.messages[messageIndex].content === '') {
                this.messages[messageIndex].content = '请求超时，请重试。'
              }

              resolve()
            }
          }, 60000) // 60秒超时

        } catch (error) {
          console.error('创建 EventSource 失败:', error)
          this.isLoading = false
          reject(error)
        }
      })
    },

    // 清空聊天记录
    clearChat() {
      this.$confirm('确定要清空所有聊天记录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.messages = []
        this.saveChatHistory()
        this.$message.success('聊天记录已清空')
      }).catch(() => {
      })
    },

    // 保存聊天记录到本地
    saveChatHistory() {
      localStorage.setItem('ai_chat_history', JSON.stringify(this.messages))
    },

    // 加载聊天记录
    loadChatHistory() {
      const history = localStorage.getItem('ai_chat_history')
      if (history) {
        try {
          this.messages = JSON.parse(history)
        } catch (e) {
          console.error('加载聊天记录失败:', e)
        }
      }
    },

    // 自动滚动到底部
    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // 格式化时间
    formatTime(date) {
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${hours}:${minutes}`
    },
  },
}
</script>


<style scoped>
.ai-chatbot {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2000;
  pointer-events: none;
}

.ai-chatbot > * {
  pointer-events: auto;
}

/* 聊天窗口 */
.chat-window {
  width: 380px;
  height: 600px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 头部 */
.chat-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
}

.header-left i {
  font-size: 20px;
}

.header-right {
  display: flex;
  gap: 12px;
}

.header-right i {
  cursor: pointer;
  font-size: 18px;
  transition: transform 0.2s;
}

.header-right i:hover {
  transform: scale(1.1);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f5f7fa;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  color: #e8e9fe;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 8px 0;
  font-size: 16px;
}

.empty-state .tips {
  font-size: 14px;
  color: #c0c4cc;
}

/* 消息项 */
.message-item {
  display: flex;
  margin-bottom: 20px;
  animation: fadeIn 0.3s ease;
}

.message-item.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-item.user .message-avatar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin-left: 12px;
}

.message-item.bot .message-avatar {
  background: #e8e9fe;
  color: #667eea;
  margin-right: 12px;
}

.message-avatar i {
  font-size: 20px;
}

.message-content {
  max-width: 70%;
}

.message-text {
  padding: 12px 16px;
  border-radius: 12px;
  word-wrap: break-word;
  line-height: 1.5;
  font-size: 14px;
  white-space: pre-wrap; /* 保留换行符 */
}

.message-item.user .message-text {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-item.bot .message-text {
  background: white;
  color: #303133;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.message-time {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  padding: 0 4px;
}

.message-item.user .message-time {
  text-align: right;
}

/* 打字动画 */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #667eea;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

/* 输入框 */
.chat-input {
  padding: 16px 20px;
  background: white;
  border-top: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-input .el-input {
  flex: 1;
}

.chat-input >>> .el-input__inner {
  border-radius: 20px;
  border: 1px solid #dcdfe6;
  padding: 10px 16px;
}

.chat-input >>> .el-input__inner:focus {
  border-color: #667eea;
}

.send-button {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  color: white !important;
  font-size: 20px;
  flex-shrink: 0;
  transition: all 0.3s ease;
  opacity: 1 !important;
}

.send-button:hover:not(.is-disabled),
.send-button:focus:not(.is-disabled) {
  background: linear-gradient(135deg, #5568d3 0%, #65408b 100%) !important;
  transform: scale(1.05);
}

.send-button.is-disabled {
  background: #e0e0e0 !important;
  color: #a8abb2 !important;
  cursor: not-allowed;
  transform: none;
  opacity: 0.6 !important;
}

.send-button.is-loading {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  opacity: 1 !important;
}

.send-button >>> i {
  font-size: 20px;
  color: white;
}

/* 浮动按钮 */
.chat-button {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  position: relative;
}

.chat-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 24px rgba(102, 126, 234, 0.5);
}

.chat-button i {
  font-size: 28px;
}

.unread-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #f56c6c;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
}

/* 动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.bounce-enter-active {
  animation: bounce-in 0.5s;
}

.bounce-leave-active {
  animation: bounce-out 0.3s;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounce-out {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
}
</style>